import styles from "./Presentation.module.css";
import React, {useEffect, useRef, useState} from "react";
import {getShortName, removePrefix, removePrefixNew} from "../../utils/helpers";
import {QRCodeCanvas}  from "qrcode.react";
import {ResponseApiCalls} from "../../apis/ResponseApiCalls";
import {
    ACTIVITY_TYPE_PLACEHOLDER,
    ATTENDANCE, COPIED_TEXT, LINK, NO_QR_CODE_OR_POLL_CODE_TO_SHARE,
    POLL_SHARE_MODE,
    POLL_STATE, POLL_TEXT, POLL_TYPE, SYNC_LMS_OPTIONS, WAITING_PARTICIPANTS_JOIN_TEXT,
    WEBSOCKET_CHECK_HEARTBEAT_INTERVAL,
    WEBSOCKET_INSTRUCTOR,
    WEBSOCKET_MAX_TOLERANCE_HEARTBEAT_GAP,
    WEBSOCKET_MESSAGE_REMOVE_CONNECTIONS_ACTION
} from "../../utils/constants";
import {useJoinQuizWebsocketManager} from "../../hooks/useJoinQuizWebsocketManager";
import {usePPTWebView} from "../../hooks/usePPTWebView";
import {timeSecondsToCustomNew} from "../../utils/questionUtils";
import Lottie from "react-lottie";

import {ReactComponent as TIME_LIMIT_ICON} from "../../images/time_limit_gray.svg";
import LiveClock from "../ReleaseActivityPage/animations/live_clock.json";
import LiveClockRed from "../ReleaseActivityPage/animations/live_clock_red.json";
import Countdown from "react-countdown";
import ProfileIcon from "../../components/ProfileIcon";
import ViewPresentQuestion from "./ViewPresentQuestion";
import {useClassResultsAnalysisManager} from "../../hooks/useClassResultsAnalysisManager";
import ViewPresentPoll from "./ViewPresentPoll";
import ViewPresentAttendance from "./ViewPresentAttendance";
import { ReactComponent as NoActivityFeedIcon } from "../PublishedPollPage/images/NO_ACTIVITY_FEED.svg";
import { ReactComponent as COPY_ICON } from "../../images/copy_gray.svg";
import {YuJaTooltip} from "../../components/standardization/YuJaTooltip";
import {useSynLMSConfiguration} from "../../hooks/useSynLMSConfiguration";
import CopyToClipboard from "react-copy-to-clipboard";
import {useConfig} from "../../hooks/useConfig";
import ImageViewer from "../JoinQuizPage/ImageViewer";
import {Button} from "@base-ui/react/button";



export default function ViewPresent({pollData, serialNo, resetPollData, userSettings}) {
    const {poll={}, questions=[]} = pollData;
    const {pk, sk, pollType} = poll;
    const config = useConfig();

    const pollKey = removePrefixNew(pk);
    const pollCode = removePrefixNew(sk);
    const INST_URL = getShortName() + '.' + config.host_url;

    const {getActiveStudentsInPoll, getResponsesDataByPoll} = ResponseApiCalls();
    const {parseToken} = usePPTWebView();
    const {institutionId, userId, sub: userName, } = parseToken();
    const {getRedirectUrl, canHavePollCode} = useSynLMSConfiguration();

    const [liveResponsesData, setLiveResponsesData] = useState([]);
    const [activeStudents, setActiveStudents] = useState({});
    const [queEnd, setQueEnd] = useState(Date.now());
    const [pollEnd, setPollEnd] = useState(Date.now());
    const [copied, setCopied] = useState(false);
    const [QRCodeImageURL, setQRCodeImageURL] = useState("");


    const [localState, setLocalState] = useState("START POLL");
    const timeRemainingRef = useRef(0);
    const clipBoardRef = useRef();


    const LiveClockOptions = {
        loop: true,
        autoplay: true,
        animationData: LiveClock,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        }
    };

    const LiveClockRedOptions = {
        loop: true,
        autoplay: true,
        animationData: LiveClockRed,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        }
    };

    let redirectUrl = getRedirectUrl(poll);
    let isPollCodeVisible = canHavePollCode(poll);


    const getWebSocketUrl = () => {
        return `${config.wss_base_url}?pollKey=${pollKey}&pollCode=${pollCode}&userName=${userName}&userId=${userId}&role=${WEBSOCKET_INSTRUCTOR}&institutionId=${institutionId}`;
    }


    const updateAllInfo = () => {
        getActiveStudentsInPoll(pollKey, pollCode).then(resp => {
            const {students=[]} = resp;
            const lastHeartbeat = Date.now();
            resp.students = students.map(i => {
                i.lastHeartbeat = lastHeartbeat;
                return i;
            });
            setActiveStudents(resp);
        });

        getResponsesDataByPoll(pollKey, pollCode).then(res => {
            setLiveResponsesData(res);
        });
    };

    const {
        responseCountData,
        reactionCountData,
    } = useClassResultsAnalysisManager(pollData, liveResponsesData);



    const {isOpen, websocketMsg, buildConnection, closeConnection, sendMsg} = useJoinQuizWebsocketManager(getWebSocketUrl, updateAllInfo);

    useEffect(() => {
        buildConnection();

        return () => {
            closeConnection();
        }
    }, []);

    //check inactive websocket connections
    useEffect(() => {
        const interval = setInterval( () => {
            const activeStudentsCopy = JSON.parse(JSON.stringify(activeStudents));
            const {students: studentsCopy=[]} = activeStudentsCopy;
            const now = Date.now();
            const removedConnIds = [];
            const newStudents = [];
            for (let stu of studentsCopy) {
                const {lastHeartbeat=0, sk=""} = stu;
                if (!!lastHeartbeat && now - lastHeartbeat > WEBSOCKET_MAX_TOLERANCE_HEARTBEAT_GAP) {
                    removedConnIds.push(removePrefix(sk));
                    continue;
                }

                if (!lastHeartbeat) {
                    stu.lastHeartbeat = now;
                }
                newStudents.push(stu);
            }

            if (removedConnIds.length > 0) {
                activeStudentsCopy.students = newStudents;
                setActiveStudents(activeStudentsCopy);
                sendMsg({action: WEBSOCKET_MESSAGE_REMOVE_CONNECTIONS_ACTION, connectionIds: removedConnIds});
            }

        }, WEBSOCKET_CHECK_HEARTBEAT_INTERVAL);

        return () => clearInterval(interval);
    }, [activeStudents]);


    //back up policy
    useEffect(() => {
        let intervalId = 0;

        if (!isOpen) {
            updateAllInfo();
            intervalId = setInterval(() => {
                updateAllInfo();
            }, 15000);
        }

        return () => {
            if (!!intervalId) {
                clearInterval(intervalId);
            }
        }

    }, [isOpen]);

    useEffect(() => {
        if(!!websocketMsg.eventMessage && websocketMsg.eventMessage === "connect") {
            const now = Date.now();
            let {student={}, userResponse={}} = websocketMsg;
            const copy = JSON.parse(JSON.stringify(activeStudents));
            const {students=[]} = copy;
            const existedStudent = students.find(i => i.sk === student.sk);
            if (!!existedStudent) {
                existedStudent.lastHeartbeat = now;
            } else {
                student.lastHeartbeat = now;
                students.push(student);
            }

            setActiveStudents(copy);

            let copyData = JSON.parse(JSON.stringify(liveResponsesData));
            if (Object.entries(userResponse).length > 0) {
                copyData = copyData.filter(r => r.pollKey === userResponse.pollKey && r.pollSortKey !== userResponse.pollSortKey);
                copyData.push(userResponse);
                setLiveResponsesData(copyData);
            }
        }
        else if(!!websocketMsg.eventMessage && websocketMsg.eventMessage === "disconnect") {
            let {student={}} = websocketMsg;
            const copy = JSON.parse(JSON.stringify(activeStudents));
            let {students=[]} = copy;
            students = students.filter(i => i.sk !== student.sk);
            copy.students = students;
            setActiveStudents(copy);
        }
        else if(!!websocketMsg.eventMessage && websocketMsg.eventMessage === "updateResponse") {
            let {questionResponses=[], userResponse} = websocketMsg;
            if (questionResponses.length === 0) {
                return;
            }

            let responses = [...questionResponses];
            if (!!userResponse) {
                responses.push(userResponse);
            }

            const keySet = new Set(
                responses.map(response => {
                    const {pollKey="", pollSortKey=""} = response;
                    return `${pollKey}-${pollSortKey}`;
                })
            );

            let copyData = JSON.parse(JSON.stringify(liveResponsesData));
            copyData = copyData.filter(
                ({ pollKey="", pollSortKey="" }) => !keySet.has(`${pollKey}-${pollSortKey}`)
            );

            for (const response of responses) {
                copyData.push(response);
            }
            setLiveResponsesData(copyData);
        }
        else if (!!websocketMsg.eventMessage && websocketMsg.eventMessage === "updateReaction") {
            let {userResponse={}} = websocketMsg;
            let copyData = JSON.parse(JSON.stringify(liveResponsesData));
            copyData = copyData.filter(r => r.pollKey === userResponse.pollKey && r.pollSortKey !== userResponse.pollSortKey);
            copyData.push(userResponse);
            setLiveResponsesData(copyData);
        }
        else if (!!websocketMsg.eventMessage && websocketMsg.eventMessage === "heartbeat") {
            const now = Date.now();
            let {student={}} = websocketMsg;
            if (!student) {
                return;
            }
            const copy = JSON.parse(JSON.stringify(activeStudents));
            const {students=[]} = copy;
            const existedStudent = students.find(i => i.sk === student.sk);
            if (!!existedStudent) {
                existedStudent.lastHeartbeat = now;
            } else {
                student.lastHeartbeat = now;
                students.push(student);
            }

            setActiveStudents(copy);
        }
        else if (!!websocketMsg.eventMessage && websocketMsg.eventMessage === "reattemptPoll") {
            let {userResponse={}, questionResponses=[]} = websocketMsg;
            if (Object.entries(userResponse).length === 0) {
                return;
            }

            let responses = [...questionResponses];
            responses.push(userResponse);


            const keySet = new Set(
                responses.map(response => {
                    const {pollKey="", pollSortKey=""} = response;
                    return `${pollKey}-${pollSortKey}`;
                })
            );

            let copyData = JSON.parse(JSON.stringify(liveResponsesData));
            copyData = copyData.filter(
                ({ pollKey="", pollSortKey="" }) => !keySet.has(`${pollKey}-${pollSortKey}`)
            );

            for (const response of responses) {
                copyData.push(response);
            }
            setLiveResponsesData(copyData);
        }
    }, [websocketMsg]);


    useEffect(() => {
        const {liveQuestionNo = 0, pollState = '', pollShareMode, liveQuestionTime = 0, pollTimeLimit, index5Pk} = poll;


        if (pollState === POLL_STATE.STOPPED || pollState === POLL_STATE.STOPPED_ALL) {
            setLocalState("START POLL");
        } else if (pollState === POLL_STATE.SHARED_ALL) {
            let pollEndPoint= liveQuestionTime + pollTimeLimit * 1000;
            const currentTime = Date.now();

            if (liveQuestionTime > currentTime) {
                setPollEnd(pollEndPoint);
                setLocalState("GET_READY_ALL");
                return;
            }

            //unlimited share all
            if(!pollTimeLimit) {
                setPollEnd(pollEndPoint);
                setLocalState("SHARED_ALL");
                return;
            }

            if (pollEndPoint > currentTime) {
                setPollEnd(pollEndPoint);
                setLocalState("SHARED_ALL");
                return;
            }
        }
        // else if (pollShareMode === POLL_SHARE_MODE.SCHEDULE) {
        //     const currentTime = new Date();
        //
        //     if (poll.pollState === POLL_STATE.STOPPED_SCHEDULED
        //         || (poll.startTime && poll.endTime && (new Date(poll.endTime) < currentTime))
        //     ) {
        //         setLocalState("TIMEUP_ALL");
        //         return;
        //     }
        //
        //     if (poll.startTime && poll.endTime && (new Date(poll.endTime) > currentTime && currentTime > new Date(poll.startTime) )) {
        //         setPollEnd(new Date(poll.endTime).getTime());
        //         setLocalState("SHARED_ALL");
        //         return;
        //     }
        //
        //     if (poll.startTime && poll.endTime && new Date(poll.endTime) > currentTime) {
        //         setPollEnd(new Date(poll.endTime).getTime());
        //     }
        // }
        else {
            if (liveQuestionNo < 1) {
                return;
            }


            const liveQue = questions[liveQuestionNo - 1];

            const { timeLimit = 0 } = liveQue;
            let queEndPoint = pollType === POLL_TYPE.ATTENDANCE ? new Date(poll.endTime).getTime() : liveQuestionTime + timeLimit * 1000;
            const currentTime = Date.now();




            if (liveQuestionTime > currentTime) {
                // if (liveQuestionNo === parseInt(serialNo)) {
                    setQueEnd(queEndPoint);
                    setLocalState("GET_READY");
                // }

                return;
            }

            if (queEndPoint > currentTime) {
                // if (liveQuestionNo === parseInt(serialNo)) {
                    setQueEnd(queEndPoint);
                    setLocalState("SHARED");
                // }
                return;
            }

            if (poll.pollType === POLL_TYPE.MERGED_POLL && !timeLimit) {// untimed share each
                // if (liveQuestionNo === parseInt(serialNo)) {
                    setQueEnd(Date.now());
                    setLocalState("SHARED");
                // }
                return;
            }

            if (poll.pollType === POLL_TYPE.ATTENDANCE && !poll.endTime ) {// untimed attendance
                // if (liveQuestionNo === parseInt(serialNo)) {
                    setQueEnd(Date.now());
                    setLocalState("SHARED");
                // }
                return;
            }
        }

    }, [pollData]);

    useEffect(() => {
        if (!redirectUrl) {
            return;
        }


        const canvas = document.querySelector(`#QR${pollCode}`);

        if (!canvas) {
            return;
        }

        const backgroundColor = "#FFFFFF";

        const qrCodeWidth = canvas.width;
        const qrCodeHeight = canvas.height;

        const padding = 10;

        const newWidth = qrCodeWidth + 2 * padding;
        const newHeight = qrCodeHeight + 2 * padding;

        const newCanvas = document.createElement("canvas");
        newCanvas.width = newWidth;
        newCanvas.height = newHeight;
        const newContext = newCanvas.getContext("2d");

        newContext.fillStyle = backgroundColor;
        newContext.fillRect(0, 0, newWidth, newHeight);

        newContext.drawImage(canvas, padding, padding, qrCodeWidth, qrCodeHeight);

        const pngUrl = newCanvas.toDataURL("image/png");

        setQRCodeImageURL(pngUrl);
    }, [localState]);



    const renderTimer = ({ days, hours, minutes, seconds }) => {
        const question = pollType === POLL_TYPE.ATTENDANCE
            ? questions[0]
            : questions[poll.liveQuestionNo ? poll.liveQuestionNo - 1 : 0];

        if (localState !== 'GET_READY_ALL'
            && pollType === POLL_TYPE.MERGED_POLL
            && poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL
            && !poll.pollTimeLimit
        ) { //unlimited share all
            return <></>;
        }


        if (pollType === POLL_TYPE.ATTENDANCE && !poll.endTime) { //unlimited attendance
            return <></>;
        }

        if (localState !== 'GET_READY'
            && pollType === POLL_TYPE.MERGED_POLL
            && poll.pollShareMode === POLL_SHARE_MODE.SHARE_EACH
            && !question?.timeLimit
        ) { //unlimited share each
            return <></>;
        }

        //
        // if (poll.pollShareMode === POLL_SHARE_MODE.SCHEDULE) {
        //     const currentTime = new Date();
        //
        //     if (poll.pollState === POLL_STATE.STOPPED_SCHEDULED || (poll.startTime && poll.endTime && (new Date(poll.endTime) < currentTime))) {
        //         if (localState !== "TIMEUP_ALL") {
        //             setLocalState("TIME_UP_ALL");
        //         }
        //     } else if (poll.startTime && poll.endTime && (new Date(poll.endTime) > currentTime && currentTime > new Date(poll.startTime) )) {
        //         if (localState !== "SHARED_ALL") {
        //             setPollEnd(new Date(poll.endTime).getTime());
        //             setLocalState("SHARED_ALL");
        //         }
        //     }
        //
        //     return <></>;
        // }



        if (localState === 'TIMEUP_ALL') {
            return (
                <span className={styles.countDownContainer}>
                    <TIME_LIMIT_ICON style={{width: 40, height: 40}}/> 00:00:00
                </span>
            );
        }

        if (localState === 'TIMEUP') {
            return (
                <span className={styles.countDownContainer}>
                    <TIME_LIMIT_ICON style={{width: 40, height: 40}}/> 00:00:00
                </span>
            );
        }

        const hoursView = ('00' + ((days * 24) + hours)).slice(-2);
        const minutesView = ('00' + minutes).slice(-2);
        const secondsView = ('00' + seconds).slice(-2);
        const urgent = days === 0 && hours === 0 && minutes === 0 && seconds < 6 && seconds !== 0;

        const time = (seconds + minutes * 60 + hours * 3600 + days * 86400);
        timeRemainingRef.current = time;


        if (localState === "START POLL"
            && (pollType === POLL_TYPE.ATTENDANCE
                || poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL)
        ) {
            return <></>;
        }

        if (localState === "START POLL" && !!question.timeLimit) {
            return (
                <span className={styles.countDownContainer}>
                    <TIME_LIMIT_ICON style={{width: 40, height: 40}}/>
                    {timeSecondsToCustomNew(question.timeLimit)}
                </span>
            );
        }



        if (localState === "GET_READY_ALL" && time > poll.pollTimeLimit) {
            return (
                <span className={styles.countDownContainer}>
                    Get Ready: {time - poll.pollTimeLimit - 1}
                </span>
            );
        }

        if (localState === "GET_READY" && time >  question.timeLimit) {
            if (!!serialNo && poll.liveQuestionNo !== parseInt(serialNo)) {
                return (
                    <span className={styles.countDownContainer}>
                        <TIME_LIMIT_ICON style={{width: 40, height: 40}}/>
                        {timeSecondsToCustomNew(question.timeLimit)}
                    </span>
                );
            } else {
                return (
                    <span className={styles.countDownContainer}>
                        Get Ready: {time - question.timeLimit - 1}
                    </span>
                );
            }
        }



        // pad with 2 leading zeros
        if (time && poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL && time <= poll.pollTimeLimit) {
            setLocalState('SHARED_ALL');
            return (
                <span className={styles.countDownContainer} id="shareCountDown">
                    <Lottie
                        options={!!urgent ? LiveClockRedOptions : LiveClockOptions}
                        height={40}
                        width={40}
                        isClickToPauseDisabled={true}
                        style={{margin: 0}}
                    />
                    {hoursView}:{minutesView}:{secondsView}
                </span>
            );
        }

        if (time && poll.pollShareMode === POLL_SHARE_MODE.SHARE_EACH && time <= question.timeLimit) {
            setLocalState('SHARED');

            if (!!serialNo && poll.liveQuestionNo !== parseInt(serialNo)) {
                return (
                    <span className={styles.countDownContainer}>
                        <TIME_LIMIT_ICON style={{width: 40, height: 40}}/>
                        {timeSecondsToCustomNew(question.timeLimit)}
                    </span>
                );
            } else {
                return (
                    <span className={styles.countDownContainer} id="shareCountDown">
                         <Lottie
                             options={!!urgent ? LiveClockRedOptions : LiveClockOptions}
                             height={40}
                             width={40}
                             isClickToPauseDisabled={true}
                             style={{margin: 0}}
                         />
                        {hoursView}:{minutesView}:{secondsView}
                    </span>
                );
            }

        }

        return <></>;
    };


    const timeUp = () => {

        if (pollType === POLL_TYPE.MERGED_POLL
            && poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL
            && !poll.pollTimeLimit
        ) { //unlimited share all
            if (poll.pollState !== POLL_STATE.STOPPED_ALL) {
                setLocalState("SHARED_ALL");
            }
            return;
        }


        if (pollType === POLL_TYPE.ATTENDANCE && !poll.endTime) { //unlimited attendance
            if (poll.pollState !== POLL_STATE.STOPPED) {
                setLocalState( "SHARED");
            }
            return;
        }

        if (pollType === POLL_TYPE.MERGED_POLL
            && poll.pollShareMode === POLL_SHARE_MODE.SHARE_EACH
            && !questions[poll.liveQuestionNo -1]?.timeLimit
        ) { //unlimited share each
            if (poll.pollState !== POLL_STATE.STOPPED ) {
                setLocalState("SHARED");
            }
            return;
        }


        if (poll.pollType === POLL_TYPE.ATTENDANCE) {
            setLocalState("TIMEUP");
        } else if (poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL) {
            setLocalState("TIMEUP_ALL");
        } else {
            setLocalState("TIMEUP");
        }

        setTimeout(() => {
            setLocalState("START POLL");
        }, 2000);
    };


    const renderActivityFeed = () => {
        const {students=[]} = activeStudents;
        let overviewStudents = students.slice(0, 4);

        return (
            <div
                className={styles.activityFeedCard}
                // style={{maxHeight: "calc(100% - 75px)"}}
                style={{flex: 1, minHeight: 0}}
            >
                <div className={styles.column} style={{padding: 15, gap: 15}}>
                    <span style={{fontSize: 20, fontWeight: "600", lineHeight: "150%"}}>
                        {"View Participants"}
                    </span>



                    {!students.length &&
                        <NoActivityFeedIcon width={100} height={100} style={{alignSelf: 'center', flexShrink: 0}}/>
                    }

                    {!students.length &&
                        <div className={styles.activityFeedNotification}>
                            { WAITING_PARTICIPANTS_JOIN_TEXT}
                        </div>
                    }


                    {students.length > 1 &&
                        <div className={styles.row}>
                            {overviewStudents.map((element, index) =>
                                <div
                                    key={index}
                                    style={{
                                        transform: `translateX(-${12 * index}px)`,
                                        borderRadius: "50%",
                                        backgroundColor: "#FFF",
                                        padding: 2,
                                        zIndex: overviewStudents.length - index
                                    }}
                                >
                                    <ProfileIcon size={36} initialLetter={decodeURI(element.userName)[0]}/>
                                </div>
                            )}
                            {students.length > overviewStudents.length &&
                                <div
                                    className={styles.row}
                                    style={{
                                        justifyContent: "center",
                                        alignItems: "center",
                                        width: 36,
                                        height: 36,
                                        borderRadius: "50%",
                                        border: "1px solid #D5DADD",
                                        backgroundColor: "#FFF",
                                        transform: `translateX(-${12 * overviewStudents.length}px)`,
                                        zIndex: 0
                                    }}
                                >
                                    {students.length}
                                </div>
                            }
                        </div>
                    }

                    {!!students.length &&
                        <span style={{fontSize: 16, lineHeight: "150%", color: "#363636"}}>
                            {"Notifications"}
                        </span>
                    }
                </div>

                {!!students.length &&
                    <div className={styles.column} style={{overflowY: "auto"}}>
                        {students.map((element, index) => {
                            let name = "Guest"
                            if (!!element.userName) {
                                name = decodeURI(element.userName);
                            }

                            let date = element.index4Sk ? new Date(removePrefixNew(element.index4Sk)) : null;
                            return (
                                <div
                                    key={index}
                                    className={`${styles.row} ${styles.activityFeedItem}`}
                                    style={{gap: 10}}
                                >
                                    <ProfileIcon size={36} initialLetter={name[0]}/>
                                    <div className={styles.column}>
                                        <span style={{fontSize: 16, lineHeight: "150%", color: "#000"}}>{name}</span>
                                        <span>
                                            {
                                                (date
                                                        ? date.toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                        }).toUpperCase()
                                                        : "Unknown time"
                                                )
                                                + " | Joined the "
                                                + (pollType === POLL_TYPE.ATTENDANCE ? ATTENDANCE : POLL_TEXT)
                                            }
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                }


            </div>
        );
    }







    return (
        <div className={styles.row} style={{width: '100%', height: '100%'}}>
            {localState === "START POLL" &&
                <div
                    className={`${styles.leftPart} ${styles.column}`}
                    style={{alignItems: "center"}}
                >
                    <div style={{flex: 1}}/>

                    {!!redirectUrl &&
                        <>
                            <YuJaTooltip tooltipStyle={{maxWidth: 300}} text={poll.pollTitle}>
                                <span
                                    style={{fontSize: 18, fontWeight: "700", maxWidth: "100%", textAlign: "center"}}
                                >{poll.pollTitle}</span>
                            </YuJaTooltip>
                            {isPollCodeVisible &&
                                <>
                                    <span className={styles.joinText}>{`Join at ${INST_URL}`}</span>
                                    <span className={styles.code}>{"Use Code: " + pollCode}</span>
                                </>
                            }


                            <div className={styles.QRCodeCircle} style={{marginTop: 25}}>
                                <QRCodeCanvas
                                    id={`QR${pollCode}`}
                                    size={130}
                                    renderAs="canvas"
                                    value={redirectUrl}
                                />
                            </div>
                        </>
                    }

                    {!redirectUrl && poll.syncLmsOption === SYNC_LMS_OPTIONS.EMBEDDED &&
                        <>
                            <YuJaTooltip tooltipStyle={{maxWidth: 300}} text={poll.pollTitle}>
                                <span
                                    style={{
                                        fontSize: 18, fontWeight: "700", maxWidth: "100%",
                                        whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"
                                    }}
                                >{poll.pollTitle}</span>
                            </YuJaTooltip>

                            <div className={styles.NoQRCodeRect}>
                                <span style={{ maxWidth: "100%"}}>
                                    {NO_QR_CODE_OR_POLL_CODE_TO_SHARE
                                        .replaceAll(
                                            ACTIVITY_TYPE_PLACEHOLDER,
                                            pollType === POLL_TYPE.ATTENDANCE ? ATTENDANCE : POLL_TEXT
                                        )
                                        // + (` from ${poll.lmsHomeUrl}`)
                                    }
                                </span>

                                <YuJaTooltip
                                    text={COPIED_TEXT}
                                    placement={"bottom"}
                                    tooltipStyle={{maxWidth: 240}}
                                    disabled={!copied}
                                    onOpenChangeCb={(isOpen) => {
                                        if (!isOpen) {
                                            setCopied(false);
                                        }
                                    }}
                                >
                                    <Button
                                        style={{display: "inline-block", marginRight: 5}}
                                        onClick={() => clipBoardRef.current.onClick()}
                                    >
                                        <CopyToClipboard
                                            ref={clipBoardRef}
                                            text={!!poll.lmsCourseUrl ? poll.lmsCourseUrl : poll.lmsHomeUrl}
                                            onCopy={() => setCopied(true)}
                                        >
                                            <span
                                                className={styles.row}
                                                style={{cursor: "pointer", width: "100%"}}
                                            >
                                                <span
                                                    style={{
                                                        color: "#434343",
                                                        fontSize: 15,
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    {LINK.charAt(0).toUpperCase() + LINK.slice(1)}:
                                                </span>
                                                <span className={styles.row} style={{flex: 1, minWidth: 0}}>
                                                    <span
                                                        className={styles.link}
                                                        style={{flexShrink: 1, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"}}
                                                    >
                                                        {!!poll.lmsCourseUrl ? poll.lmsCourseUrl : poll.lmsHomeUrl}
                                                    </span>
                                                    <COPY_ICON style={{marginLeft: 5}}/>
                                                </span>
                                            </span>
                                        </CopyToClipboard>
                                    </Button>
                                </YuJaTooltip>
                            </div>
                            {/*<span style={{fontSize: 15, fontWeight: "600", maxWidth: "100%", textAlign: "center", marginTop: 15}}>*/}
                            {/*    {"Participants have to attempt the poll from "}*/}
                            {/*    <CopyToClipboard*/}
                            {/*        text={poll.lmsHomeUrl}*/}
                            {/*        onCopy={() => notifySuccess("Link copied to clipboard.")}*/}
                            {/*    >*/}
                            {/*        <span*/}
                            {/*            className={styles.link}*/}
                            {/*            role={TEXTBOX}*/}
                            {/*            tabIndex={0}*/}
                            {/*            onClick={(e) => e.stopPropagation()}*/}
                            {/*        >*/}
                            {/*            {poll.lmsHomeUrl}*/}
                            {/*            <COPY_ICON/>*/}
                            {/*        </span>*/}
                            {/*    </CopyToClipboard>*/}

                            {/*</span>*/}
                        </>
                    }


                    {/*{!!redirectUrl &&*/}
                    {/*    <>*/}
                    {/*        <span style={{fontSize: 25, fontWeight: "700", maxWidth: "100%", textAlign: "center"}}>*/}
                    {/*            {poll.pollTitle}*/}
                    {/*        </span>*/}
                    {/*            <span style={{fontSize: 15, fontWeight: "600", maxWidth: "100%", textAlign: "center", marginTop: 15}}>*/}
                    {/*                {NO_QR_CODE_OR_POLL_CODE_TO_SHARE*/}
                    {/*                    .replaceAll(*/}
                    {/*                        ACTIVITY_TYPE_PLACEHOLDER,*/}
                    {/*                        pollType === POLL_TYPE.ATTENDANCE ? ATTENDANCE : POLL_TEXT*/}
                    {/*                    )*/}
                    {/*                    // + (` from ${poll.lmsHomeUrl}`)*/}
                    {/*                }*/}
                    {/*            </span>*/}

                    {/*            <YuJaTooltip*/}
                    {/*                text={!copied ? "" : COPIED_TEXT}*/}
                    {/*                placement={"bottom"}*/}
                    {/*                tooltipStyle={{maxWidth: 240}}*/}
                    {/*                disabled={!copied}*/}
                    {/*            >*/}
                    {/*                <CopyToClipboard*/}
                    {/*                    text={"https://panotesting.instructure.com"}*/}
                    {/*                    onMouseLeave={() => setCopied(false)}*/}
                    {/*                    onCopy={() => setCopied(true)}*/}
                    {/*                >*/}
                    {/*                    <span*/}
                    {/*                        style={{lineHeight: 1.5, cursor: "pointer"}}*/}
                    {/*                    >*/}
                    {/*                        <span*/}
                    {/*                            style={{*/}
                    {/*                                color: "#434343",*/}
                    {/*                                fontSize: 15,*/}
                    {/*                                fontWeight: "600",*/}
                    {/*                                marginRight: 5*/}
                    {/*                            }}*/}
                    {/*                        >*/}
                    {/*                            {LINK.charAt(0).toUpperCase() + LINK.slice(1)}:*/}
                    {/*                        </span>*/}
                    {/*                        <span className={styles.link}>*/}
                    {/*                            {"https://panotesting.instructure.com"}*/}
                    {/*                        </span>*/}
                    {/*                        <COPY_ICON style={{marginLeft: 5}}/>*/}
                    {/*                    </span>*/}
                    {/*                </CopyToClipboard>*/}
                    {/*            </YuJaTooltip>*/}

                    {/*    </>*/}
                    {/*}*/}


                    <div style={{flex: 1}}/>

                </div>
            }

            {localState !== "START POLL" &&
                <div
                    className={`${styles.leftPart} ${styles.column}`}
                    style={{alignItems: "center", gap: 15}}
                >
                    <span style={{display: "none"}}>{JSON.stringify(activeStudents)}</span>
                    <Countdown
                        className="pollPanelTimerText"
                        autoStart={
                            ["SHARED_ALL", "SHARED", "GET_READY_ALL", "GET_READY"].includes(localState)
                        }
                        date={poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL ? pollEnd : queEnd}
                        key={poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL ? pollEnd : queEnd}
                        renderer={renderTimer}
                        onComplete={timeUp}
                    />


                    {!!redirectUrl &&
                        <div
                            className={styles.activityFeedCard}
                            style={{
                                display: "grid",
                                alignItems: "center",
                                padding: 15,
                                rowGap: 15,
                                gridTemplateColumns: "auto 1fr"
                            }}
                        >
                            <span className={styles.joinText} style={{gridColumn: "1 / 3"}}>{`Join at ${INST_URL}`}</span>
                            <ImageViewer
                                size={65}
                                alt={"QR Code"}
                                imageURL={QRCodeImageURL}
                            />


                            <QRCodeCanvas
                                id={`QR${pollCode}`}
                                size={512}
                                renderAs="canvas"
                                style={{display: "none",}}
                                value={redirectUrl}
                            />

                            {/*<QRCodeCanvas*/}
                            {/*    id={`QR${pollCode}`}*/}
                            {/*    size={65}*/}
                            {/*    renderAs="canvas"*/}
                            {/*    value={redirectUrl}*/}
                            {/*/>*/}

                            <div style={{textAlign: "end"}}>
                                <span
                                    className={styles.code}
                                    style={{fontSize: 18, whiteSpace: "nowrap"}}
                                >{"Use Code: " + pollCode}</span>
                            </div>

                        </div>
                    }


                    {!redirectUrl && poll.syncLmsOption === SYNC_LMS_OPTIONS.EMBEDDED &&
                        <div
                            className={styles.activityFeedCard}
                            style={{
                                height: "fit-content",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: 15,
                                gap: 15
                            }
                            }>
                            <span style={{maxWidth: "100%"}}>
                                {NO_QR_CODE_OR_POLL_CODE_TO_SHARE
                                    .replaceAll(
                                        ACTIVITY_TYPE_PLACEHOLDER,
                                        pollType === POLL_TYPE.ATTENDANCE ? ATTENDANCE : POLL_TEXT
                                    )
                                    // + (` from ${poll.lmsHomeUrl}`)
                                }
                            </span>

                            <YuJaTooltip
                                text={!copied ? "" : COPIED_TEXT}
                                placement={"bottom"}
                                tooltipStyle={{maxWidth: 240}}
                                disabled={!copied}
                                onOpenChangeCb={(isOpen) => {
                                    if (!isOpen) {
                                        setCopied(false);
                                    }
                                }}
                            >
                                <CopyToClipboard
                                    text={!!poll.lmsCourseUrl ? poll.lmsCourseUrl : poll.lmsHomeUrl}
                                    onCopy={() => setCopied(true)}
                                >
                                    <Button
                                        className={styles.row}
                                        style={{lineHeight: 1.5, cursor: "pointer", width: "100%"}}
                                        onClick={() => clipBoardRef.current.onClick()}
                                    >
                                        <span
                                            style={{
                                                color: "#434343",
                                                fontSize: 15,
                                                fontWeight: "600",
                                                marginRight: 5
                                            }}
                                        >
                                            {LINK.charAt(0).toUpperCase() + LINK.slice(1)}:
                                        </span>
                                        <span className={styles.row} style={{flex: 1, minWidth: 0}}>
                                            <span
                                                className={styles.link}
                                                style={{
                                                    flexShrink: 1,
                                                    whiteSpace: "nowrap",
                                                    textOverflow: "ellipsis",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                {!!poll.lmsCourseUrl ? poll.lmsCourseUrl : poll.lmsHomeUrl}
                                            </span>
                                            <COPY_ICON style={{marginLeft: 5, flexShrink: 0}}/>
                                        </span>
                                    </Button>
                                </CopyToClipboard>
                            </YuJaTooltip>
                        </div>
                    }

                    {renderActivityFeed()}
                    {/*<div style={{flex: 1}}/>*/}

                </div>
            }


            {poll.pollShareMode === POLL_SHARE_MODE.SHARE_EACH && poll.pollType !== POLL_TYPE.ATTENDANCE &&
                <ViewPresentQuestion
                    pollData={pollData}
                    question={questions.find(q => q.serialNo === parseInt(serialNo))}
                    responseCountData={responseCountData}
                    reactionCountData={reactionCountData}
                    localState={localState}
                    resetPollData={resetPollData}
                />
            }

            {poll.pollShareMode === POLL_SHARE_MODE.SHARE_ALL &&
                <ViewPresentPoll
                    pollData={pollData}
                    localState={localState}
                    setLocalState={setLocalState}
                    resetPollData={resetPollData}
                    responseCountData={responseCountData}
                    reactionCountData={reactionCountData}
                    userSettings={userSettings}
                />
            }

            {poll.pollType === POLL_TYPE.ATTENDANCE &&
                <ViewPresentAttendance
                    pollData={pollData}
                    localState={localState}
                    setLocalState={setLocalState}
                    resetPollData={resetPollData}
                    userSettings={userSettings}
                />
            }
        </div>
    );
}